import { useMemo, useState, type ReactNode } from "react";
import {
  Button,
  Icon,
  Menu,
  SegmentedControl,
  Table,
  Toolbar,
  useToast,
  type TableColumn,
  type TableDensity,
} from "@evolua/ui";
import { downloadCsv } from "./export-csv.js";

export type DataTableColumn<T> = TableColumn<T> & {
  label: string;
  exportValue: (row: T) => string;
  sortable?: boolean;
  defaultVisible?: boolean;
};

export type DataTableProps<T> = {
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[];
  getRowId: (row: T) => string;
  caption?: string;
  emptyMessage?: string;
  sort?: string;
  onSortChange?: (sort: string) => void;
  sortField?: string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  exportFilename?: string;
  onExport?: () => void | Promise<void>;
  exportBusy?: boolean;
  exportLabel?: string;
  toolbarAside?: ReactNode;
};

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  caption,
  emptyMessage,
  sort,
  onSortChange,
  sortField = "fullName",
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  exportFilename = "export.csv",
  onExport,
  exportBusy = false,
  exportLabel = "Exportar CSV",
  toolbarAside,
}: DataTableProps<T>) {
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());
  const [density, setDensity] = useState<TableDensity>("comfortable");
  const toast = useToast();

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.defaultVisible !== false && !hidden.has(c.id)),
    [columns, hidden],
  );

  const tableColumns: TableColumn<T>[] = visibleColumns.map((col) => {
    const asc = `${sortField}:asc`;
    const desc = `${sortField}:desc`;
    const active = sort === asc || sort === desc;
    const sortDirection =
      sort === asc ? "ascending" : sort === desc ? "descending" : "none";
    const next = sort === asc ? desc : asc;
    const header =
      col.sortable && onSortChange ? (
        <button
          type="button"
          className="evolua-table-sort"
          onClick={() => onSortChange(next)}
        >
          {col.header}
          {active ? (sort === asc ? " ↑" : " ↓") : ""}
        </button>
      ) : (
        col.header
      );
    return {
      id: col.id,
      cell: col.cell,
      header,
      ...(col.sortable ? { sortDirection } : {}),
      ...(col.width ? { width: col.width } : {}),
      ...(col.align ? { align: col.align } : {}),
      ...(col.priority ? { priority: col.priority } : {}),
      ...(col.label ? { label: col.label } : {}),
    };
  });

  function toggleColumn(id: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function exportVisible() {
    if (onExport) {
      await onExport();
      toast.push("Exportação iniciada.", "success");
      return;
    }
    const headers = visibleColumns.map((c) => c.label);
    const data = rows.map((row) => visibleColumns.map((c) => c.exportValue(row)));
    downloadCsv(exportFilename, headers, data);
    toast.push("CSV exportado.", "success");
  }

  return (
    <div className="evolua-data-table">
      <Toolbar>
        <Menu
          label="Colunas visíveis"
          trigger={
            <>
              <Icon name="columns" size={16} />
              Colunas
            </>
          }
          items={columns.map((col) => ({
            id: col.id,
            label: col.label,
            onSelect: () => toggleColumn(col.id),
          }))}
        />
        <SegmentedControl
          ariaLabel="Densidade da tabela"
          value={density}
          onChange={(id) => setDensity(id as TableDensity)}
          items={[
            { id: "comfortable", label: "Confortável" },
            { id: "compact", label: "Compacta" },
          ]}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={exportBusy}
          iconStart={<Icon name="download" size={16} />}
          onClick={() => void exportVisible()}
        >
          {exportLabel}
        </Button>
        {toolbarAside}
      </Toolbar>

      <div className="evolua-data-table__scroll">
        <Table
          {...(caption ? { caption } : {})}
          columns={tableColumns}
          rows={rows}
          getRowId={getRowId}
          density={density}
          {...(emptyMessage ? { emptyMessage } : {})}
        />
      </div>

      {hasNextPage && onLoadMore ? (
        <p className="evolua-mt">
          <Button
            type="button"
            variant="secondary"
            loading={Boolean(isFetchingNextPage)}
            onClick={onLoadMore}
          >
            Carregar mais
          </Button>
        </p>
      ) : null}
    </div>
  );
}
